import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CirclePlus,
  ClipboardCheck,
  Minus,
  MoveHorizontal,
  RotateCcw,
  Send,
  ShoppingBag,
  X,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { getError } from "@/api";
import { MovementDetailSheet } from "@/components/MovementDetailSheet";
import { ProductCombobox } from "@/components/ProductCombobox";

const saleChoices = [
  ["vente_directe", "Vente directe", "Facture réglée intégralement par le bénéficiaire."],
  ["vente_credit", "Vente à crédit", "Facture client avec suivi des paiements."],
  ["don", "Don", "Facture au montant de 0 GNF pour le bénéficiaire."],
];
const movementLabels = {
  entree: "Entrée",
  sortie: "Sortie",
  transfert: "Transfert",
  initial: "Stock initial",
};

const emptyMovement = {
  movement_type: "entree",
  entry_type: "stock_initial",
  date_mode: "auto",
  movement_date: "",
  warehouse_id: "",
  destination_warehouse_id: "",
  notes: "",
  lines: [],
};

export default function MovementsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preparedProductId = searchParams.get("product_id");
  const preparedWarehouseId = searchParams.get("warehouse_id");
  const preparedMovementType = searchParams.get("movement_type");
  const preparedProductHandled = useRef(false);
  const preparedWarehouseHandled = useRef(false);
  const quantityInputRef = useRef(null);
  const productInputRef = useRef(null);
  const confirmationSubmitRef = useRef(null);
  const submissionKeyRef = useRef("");
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [zones, setZones] = useState([]);
  const [warehouseQuery, setWarehouseQuery] = useState("");
  const [warehouseTypeFilter, setWarehouseTypeFilter] = useState("");
  const [warehouseZoneFilter, setWarehouseZoneFilter] = useState("");
  const [warehouseSubzoneFilter, setWarehouseSubzoneFilter] = useState("");
  const [user, setUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState({ product_id: "", quantity: "", transport_cost: "" });
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [latestCancellableMovement, setLatestCancellableMovement] = useState(null);
  const [isLatestCancellationLoading, setIsLatestCancellationLoading] = useState(false);
  const [isCancellationConfirming, setIsCancellationConfirming] = useState(false);
  const [movement, setMovement] = useState(emptyMovement);
  const [selectedMovementId, setSelectedMovementId] = useState("");

  const load = () => Promise.all([
    api.get("/products"),
    api.get("/warehouses"),
    api.get("/movements"),
    api.get("/auth/me"),
    api.get("/zones"),
  ])
    .then(([productsResponse, warehousesResponse, movementsResponse, userResponse, zonesResponse]) => {
      setProducts(productsResponse.data.filter((item) => item.active));
      setWarehouses(warehousesResponse.data);
      setHistory(movementsResponse.data);
      setUser(userResponse.data);
      setZones(zonesResponse.data);
    })
    .catch((error) => toast.error(getError(error)));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!preparedProductId || preparedProductHandled.current) {
      return;
    }

    const preparedProduct = products.find((product) => product.product_id === preparedProductId);
    if (!preparedProduct) {
      return;
    }

    setDraft({
      product_id: preparedProduct.product_id,
      quantity: "",
      transport_cost: "",
    });
    preparedProductHandled.current = true;
    toast.success(`${preparedProduct.name} est prêt à être ajouté au mouvement.`);
  }, [preparedProductId, products]);

  useEffect(() => {
    if (!preparedWarehouseId || preparedWarehouseHandled.current) {
      return;
    }

    const preparedWarehouse = warehouses.find(
      (warehouse) => warehouse.warehouse_id === preparedWarehouseId,
    );
    if (!preparedWarehouse) {
      return;
    }

    setMovement((current) => ({
      ...current,
      movement_type: preparedMovementType === "transfert" ? "transfert" : current.movement_type,
      warehouse_id: preparedWarehouse.warehouse_id,
      destination_warehouse_id: "",
      entry_type: "stock_initial",
      lines: [],
    }));
    preparedWarehouseHandled.current = true;
    toast.success(`${preparedWarehouse.name} est sélectionné comme dépôt de départ.`);
  }, [preparedMovementType, preparedWarehouseId, warehouses]);

  const filteredWarehouses = warehouses.filter((warehouse) => {
    const matchesSearch = warehouse.name
      .toLocaleLowerCase("fr-FR")
      .includes(warehouseQuery.toLocaleLowerCase("fr-FR"));
    const matchesType = !warehouseTypeFilter || warehouse.warehouse_type === warehouseTypeFilter;
    const matchesZone = !warehouseZoneFilter || warehouse.zone_id === warehouseZoneFilter;
    const matchesSubzone = !warehouseSubzoneFilter || warehouse.subzone === warehouseSubzoneFilter;

    return matchesSearch && matchesType && matchesZone && matchesSubzone;
  });
  const source = warehouses.find((item) => item.warehouse_id === movement.warehouse_id);
  const productsById = Object.fromEntries(
    products.map((product) => [product.product_id, product]),
  );
  const isEntry = movement.movement_type === "entree";
  const canEditMovementDate = ["admin_principal", "admin_delegue"].includes(user?.role);
  const canCancelLastMovement = ["admin_principal", "admin_delegue"].includes(user?.role);
  const usesSavedTransportDefault = Boolean(
    isEntry
    && source
    && (
      (!source.is_main && movement.entry_type === "stock_initial")
      || (source.is_main && movement.entry_type === "approvisionnement")
    ),
  );
  const selectedDraftProduct = products.find((product) => product.product_id === draft.product_id);
  const transportCostOrigin = usesSavedTransportDefault
    && selectedDraftProduct?.last_transport_cost_updated_at
    ? {
      warehouse: selectedDraftProduct.last_transport_cost_source_name || "dépôt principal",
      date: new Date(selectedDraftProduct.last_transport_cost_updated_at)
        .toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }),
    }
    : null;
  const commercialExit = movement.movement_type === "sortie"
    && source?.is_main
    && ["admin_principal", "responsable_stock"].includes(user?.role);
  const entryTypeLocked = Boolean(movement.warehouse_id && !source?.is_main);
  const manualDate = canEditMovementDate && movement.date_mode === "manual";
  const totalTransportCost = movement.lines.reduce(
    (sum, line) => sum + Number(line.transport_cost || 0) * Number(line.quantity || 0),
    0,
  );
  const reviewTitle = commercialExit
    ? "Facturation requise"
    : isEntry
      ? movement.entry_type === "stock_initial"
        ? "Bon de stock initial"
        : "Bon d’approvisionnement"
      : movement.movement_type === "transfert"
        ? "Bon de transfert"
        : "Bon de sortie";

  const chooseType = (movementType) => {
    setMovement((current) => ({
      ...current,
      movement_type: movementType,
      entry_type: movementType === "entree" && source?.is_main
        ? "approvisionnement"
        : "stock_initial",
      destination_warehouse_id: "",
      lines: [],
    }));
    setDraft({ product_id: "", quantity: "", transport_cost: "" });
  };

  const chooseSource = (warehouseId) => {
    const warehouse = warehouses.find((item) => item.warehouse_id === warehouseId);
    setMovement((current) => ({
      ...current,
      warehouse_id: warehouseId,
      destination_warehouse_id: "",
      entry_type: current.movement_type === "entree" && warehouse?.is_main
        ? "approvisionnement"
        : "stock_initial",
      lines: [],
    }));
    setDraft({ product_id: "", quantity: "", transport_cost: "" });
  };

  const selectProduct = (product) => {
    setDraft((current) => ({
      ...current,
      product_id: product?.product_id || "",
      transport_cost: product && usesSavedTransportDefault
        ? String(product.last_transport_cost || "")
        : "",
    }));
    if (product) {
      requestAnimationFrame(() => quantityInputRef.current?.focus());
    }
  };

  const addLine = () => {
    if (!draft.product_id || Number(draft.quantity) <= 0) {
      toast.error("Choisissez un produit et une quantité.");
      return;
    }

    const transportCost = isEntry && draft.transport_cost !== ""
      ? Number(draft.transport_cost)
      : null;
    const existing = movement.lines.find((line) => (
      line.product_id === draft.product_id
      && Number(line.transport_cost || 0) === Number(transportCost || 0)
    ));
    const lines = existing
      ? movement.lines.map((line) => {
        if (line.product_id !== draft.product_id) {
          return line;
        }

        return {
          ...line,
          quantity: line.quantity + Number(draft.quantity),
          transport_cost: line.transport_cost,
        };
      })
      : [
        ...movement.lines,
        {
          line_id: `${draft.product_id}-${Date.now()}-${movement.lines.length}`,
          product_id: draft.product_id,
          quantity: Number(draft.quantity),
          transport_cost: transportCost,
        },
      ];

    setMovement((current) => ({ ...current, lines }));
    setDraft({ product_id: "", quantity: "", transport_cost: "" });
    requestAnimationFrame(() => productInputRef.current?.focus());
  };

  const addLineOnEnter = (event) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      addLine();
    }
  };

  const submit = async () => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    try {
      if (!submissionKeyRef.current) {
        submissionKeyRef.current = crypto.randomUUID();
      }
      const payload = { ...movement, idempotency_key: submissionKeyRef.current };
      delete payload.date_mode;

      if (movement.date_mode === "auto") {
        delete payload.movement_date;
      }

      if (isEntry && movement.entry_type === "stock_initial") {
        await api.post("/initial-stock", {
          ...payload,
          notes: movement.notes || "Stock initial",
        });
      } else {
        await api.post("/movements", payload);
      }

      toast.success("Mouvement enregistré");
      submissionKeyRef.current = "";
      setMovement(emptyMovement);
      setDraft({ product_id: "", quantity: "", transport_cost: "" });
      setConfirming(false);
      load();
    } catch (error) {
      toast.error(getError(error));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const canConfirm = Boolean(
    movement.warehouse_id
    && movement.lines.length
    && (movement.movement_type !== "transfert" || movement.destination_warehouse_id),
  );

  const openConfirmation = () => {
    if (!canConfirm) {
      toast.error("Complétez le dépôt et ajoutez au moins un produit avant de contrôler.");
      return;
    }

    setConfirming(true);
  };

  useEffect(() => {
    if (!confirming) {
      return undefined;
    }

    const focusConfirmationSubmit = requestAnimationFrame(() => {
      confirmationSubmitRef.current?.focus();
    });
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !submitting) {
        setConfirming(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => {
      cancelAnimationFrame(focusConfirmationSubmit);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [confirming, submitting]);

  const prepareCommercialSale = (motif) => {
    if (!canConfirm) {
      toast.error("Ajoutez au moins un produit avant de préparer la facture.");
      return;
    }

    navigate(`/ventes?warehouse_id=${source.warehouse_id}&motif=${motif}`, {
      state: {
        preparedSale: {
          lines: movement.lines.map((line) => ({
            product_id: line.product_id,
            quantity: Number(line.quantity),
          })),
          notes: movement.notes,
          date_mode: movement.date_mode,
          movement_date: movement.movement_date,
        },
      },
    });
  };

  useEffect(() => {
    let isCurrent = true;

    if (!canCancelLastMovement || !movement.warehouse_id) {
      setLatestCancellableMovement(null);
      setIsCancellationConfirming(false);
      return () => {
        isCurrent = false;
      };
    }

    setIsLatestCancellationLoading(true);
    api.get("/movements/latest", { params: { warehouse_id: movement.warehouse_id } })
      .then((response) => {
        if (isCurrent) {
          setLatestCancellableMovement(response.data.movement);
        }
      })
      .catch((error) => {
        if (isCurrent) {
          toast.error(getError(error));
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLatestCancellationLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [canCancelLastMovement, history, movement.warehouse_id]);

  const cancelLastMovement = async () => {
    if (!latestCancellableMovement || !movement.warehouse_id) {
      return;
    }

    try {
      await api.post(`/movements/${latestCancellableMovement.movement_id}/cancel`, {
        warehouse_id: movement.warehouse_id,
        confirmation: "ANNULER",
      });
      toast.success("Le dernier mouvement a été annulé et le stock a été restauré.");
      setIsCancellationConfirming(false);
      setLatestCancellableMovement(null);
      load();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  return (
    <div className="page-enter" data-testid="movements-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">OPÉRATIONS</p>
          <h1>Mouvement de stock</h1>
          <p>Centralisez les entrées, sorties et transferts depuis un même écran.</p>
        </div>
      </div>
      <section className="movement-workspace" data-testid="movement-workspace">
        <div className="movement-form-panel">
          <div className="movement-kind-tabs" data-testid="movement-type-tabs">
            {["entree", "sortie", "transfert"].map((type) => (
              <button
                className={movement.movement_type === type ? "active" : ""}
                data-testid={`movement-type-${type}-button`}
                key={type}
                onClick={() => chooseType(type)}
                type="button"
              >
                {type}
              </button>
            ))}
          </div>
          <div className="quick-search-bar" data-testid="movement-warehouse-filters">
            <input
              data-testid="movement-warehouse-search-input"
              onChange={(event) => setWarehouseQuery(event.target.value)}
              placeholder="Rechercher un dépôt"
              value={warehouseQuery}
            />
            <select
              data-testid="movement-warehouse-type-filter"
              onChange={(event) => setWarehouseTypeFilter(event.target.value)}
              value={warehouseTypeFilter}
            >
              <option value="">Tous les types</option>
              <option value="principal">Principal</option>
              <option value="zone_conakry">Zone Conakry</option>
              <option value="zone_interieur">Zone Intérieur</option>
              <option value="livreur">Livreur</option>
            </select>
            <select
              data-testid="movement-warehouse-zone-filter"
              onChange={(event) => {
                setWarehouseZoneFilter(event.target.value);
                setWarehouseSubzoneFilter("");
              }}
              value={warehouseZoneFilter}
            >
              <option value="">Toutes les zones</option>
              {zones.map((zone) => (
                <option key={zone.zone_id} value={zone.zone_id}>{zone.name}</option>
              ))}
            </select>
            <select
              data-testid="movement-warehouse-subzone-filter"
              onChange={(event) => setWarehouseSubzoneFilter(event.target.value)}
              value={warehouseSubzoneFilter}
            >
              <option value="">Toutes les sous-zones</option>
              {zones.find((zone) => zone.zone_id === warehouseZoneFilter)?.subzones.map((subzone) => (
                <option key={subzone} value={subzone}>{subzone}</option>
              ))}
            </select>
          </div>
          <div className="form-grid">
            <label>
              Dépôt concerné
              <select
                data-testid="movement-source-warehouse-select"
                onChange={(event) => chooseSource(event.target.value)}
                value={movement.warehouse_id}
              >
                <option value="">Sélectionner</option>
                {filteredWarehouses.map((warehouse) => (
                  <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            {isEntry && (
              <label>
                Type d’entrée
                <select
                  data-testid="movement-entry-type-select"
                  disabled={!movement.warehouse_id || entryTypeLocked}
                  onChange={(event) => setMovement((current) => ({
                    ...current,
                    entry_type: event.target.value,
                  }))}
                  value={movement.entry_type}
                >
                  {source?.is_main && <option value="approvisionnement">Approvisionnement</option>}
                  <option value="stock_initial">Stock initial</option>
                </select>
                {!movement.warehouse_id && (
                  <small data-testid="movement-entry-type-pending">
                    Sélectionnez d’abord un dépôt.
                  </small>
                )}
                {source?.is_main && (
                  <small data-testid="movement-main-entry-type-help">
                    Choisissez Stock initial ou Approvisionnement.
                  </small>
                )}
                {entryTypeLocked && (
                  <small data-testid="movement-secondary-entry-type-help">
                    Stock initial est le seul type autorisé pour ce dépôt.
                  </small>
                )}
              </label>
            )}
            {movement.movement_type === "transfert" && (
              <label>
                Dépôt destinataire
                <select
                  data-testid="movement-destination-warehouse-select"
                  onChange={(event) => setMovement((current) => ({
                    ...current,
                    destination_warehouse_id: event.target.value,
                  }))}
                  value={movement.destination_warehouse_id}
                >
                  <option value="">Sélectionner</option>
                  {filteredWarehouses
                    .filter((warehouse) => warehouse.warehouse_id !== movement.warehouse_id)
                    .map((warehouse) => (
                      <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                        {warehouse.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
            {isEntry && movement.warehouse_id && !source?.is_main && (
              <p className="entry-rule-note wide" data-testid="movement-non-main-entry-rule">
                Ce dépôt reçoit ses approvisionnements par transfert. Seul le stock initial est
                disponible ici.
              </p>
            )}
            <label className="wide">
              Observation
              <textarea
                data-testid="movement-notes-input"
                onChange={(event) => setMovement((current) => ({
                  ...current,
                  notes: event.target.value,
                }))}
                placeholder="Information utile pour le document"
                value={movement.notes}
              />
            </label>
            <div className="movement-date-control wide" data-testid="movement-date-control">
              <span>Date du mouvement</span>
              <div className="movement-date-mode-buttons">
                <button
                  className={!manualDate ? "active" : ""}
                  data-testid="movement-date-auto-button"
                  onClick={() => setMovement((current) => ({
                    ...current,
                    date_mode: "auto",
                    movement_date: "",
                  }))}
                  type="button"
                >
                  Automatique
                </button>
                {canEditMovementDate && (
                  <button
                    className={manualDate ? "active" : ""}
                    data-testid="movement-date-manual-button"
                    onClick={() => setMovement((current) => ({
                      ...current,
                      date_mode: "manual",
                      movement_date: current.movement_date || new Date().toISOString().slice(0, 10),
                    }))}
                    type="button"
                  >
                    Saisie manuelle
                  </button>
                )}
              </div>
              {manualDate ? (
                <input
                  data-testid="movement-manual-date-input"
                  lang="fr-FR"
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(event) => setMovement((current) => ({
                    ...current,
                    movement_date: event.target.value,
                  }))}
                  required
                  type="date"
                  value={movement.movement_date}
                />
              ) : (
                <small data-testid="movement-auto-date-help">
                  {canEditMovementDate
                    ? "La date et l’heure actuelles seront appliquées automatiquement."
                    : "La date est automatique. Sa modification est réservée aux administrateurs."}
                </small>
              )}
            </div>
          </div>
          <>
            <div
              className={`line-adder${isEntry ? " with-transport" : ""}`}
              data-testid="movement-line-adder"
            >
                <ProductCombobox
                  inputRef={productInputRef}
                  onSelect={selectProduct}
                  products={products}
                  value={draft.product_id}
                />
                <input
                  data-testid="movement-quantity-input"
                  min="0.01"
                  onChange={(event) => setDraft((current) => ({
                    ...current,
                    quantity: event.target.value,
                  }))}
                  onKeyDown={addLineOnEnter}
                  placeholder="Qté"
                  ref={quantityInputRef}
                  step="0.01"
                  type="number"
                  value={draft.quantity}
                />
                {isEntry && (
                  <input
                    aria-label="Coût de transport unitaire"
                    data-testid="movement-transport-cost-input"
                    min="0"
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      transport_cost: event.target.value,
                    }))}
                    onKeyDown={addLineOnEnter}
                    placeholder="Coût unitaire"
                    step="1"
                    type="number"
                    value={draft.transport_cost}
                  />
                )}
                <button
                  aria-label="Ajouter le produit au mouvement"
                  data-testid="add-movement-line-button"
                  onClick={addLine}
                  title="Ajouter au mouvement"
                  type="button"
                >
                  <CirclePlus size={17} /> Ajouter
                </button>
            </div>
            {transportCostOrigin && (
              <p className="transport-cost-origin" data-testid="movement-transport-cost-origin">
                Dernier coût enregistré au {transportCostOrigin.warehouse} le {transportCostOrigin.date}.
              </p>
            )}
          </>
        </div>
        <div className="movement-review-panel" data-testid="movement-review-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">CONTRÔLE</p>
              <h2>{reviewTitle}</h2>
            </div>
            <ClipboardCheck size={20} />
          </div>
          <>
              {!movement.lines.length ? (
                <div className="empty-inline" data-testid="empty-movement-lines-message">
                  Ajoutez un ou plusieurs produits à cette opération.
                </div>
              ) : (
                <div className="line-review-list" data-testid="movement-line-review-list">
                  {movement.lines.map((line, index) => {
                    const unitTransportCost = Number(line.transport_cost || 0);
                    const lineTransportTotal = unitTransportCost * Number(line.quantity || 0);
                    const lineKey = line.line_id || `${line.product_id}-${index}`;

                    return (
                      <div data-testid={`movement-line-${lineKey}`} key={lineKey}>
                        <span>
                          <strong>
                            {products.find((product) => product.product_id === line.product_id)?.name}
                          </strong>
                          <small>
                            {isEntry
                              ? `Qté : ${line.quantity} · Unitaire : ${unitTransportCost.toLocaleString(
                                "fr-FR",
                              )} GNF · Total : ${lineTransportTotal.toLocaleString("fr-FR")} GNF`
                              : "Quantité à traiter"}
                          </small>
                        </span>
                        <b>{line.quantity}</b>
                        <button
                          aria-label="Retirer ce produit du mouvement"
                          data-testid={`remove-movement-line-${lineKey}`}
                          onClick={() => setMovement((current) => ({
                            ...current,
                            lines: current.lines.filter((item, itemIndex) => (
                              (item.line_id || `${item.product_id}-${itemIndex}`) !== lineKey
                            )),
                          }))}
                          type="button"
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {isEntry && movement.lines.length > 0 && (
                <p className="movement-transport-total" data-testid="movement-transport-total">
                  Coût total de transport : {totalTransportCost.toLocaleString("fr-FR")} GNF
                </p>
              )}
              {commercialExit ? (
                <div className="commercial-exit-choices" data-testid="commercial-exit-choices">
                  <div>
                    <p className="eyebrow">SORTIE DU DÉPÔT PRINCIPAL</p>
                    <h2>Choisissez le type de facture pour ces produits</h2>
                  </div>
                  {saleChoices.map(([motif, label, description]) => (
                    <button
                      data-testid={`commercial-exit-${motif}-button`}
                      disabled={!canConfirm}
                      key={motif}
                      onClick={() => prepareCommercialSale(motif)}
                      type="button"
                    >
                      <ShoppingBag size={18} />
                      <span>
                        <strong>{label}</strong>
                        <small>{description}</small>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <button
                    className="primary-action confirm-movement-button"
                    data-testid="confirm-movement-button"
                    disabled={!canConfirm}
                    onClick={openConfirmation}
                    type="button"
                  >
                    <CheckCircle2 size={17} /> Contrôler et valider
                  </button>
                </>
              )}
          </>
        </div>
      </section>
      {confirming && createPortal(
        <div
          aria-labelledby="movement-confirmation-title"
          aria-modal="true"
          className="movement-confirmation-modal"
          data-testid="movement-confirmation-modal"
          role="dialog"
        >
          <section className="movement-confirmation-dialog" data-testid="movement-confirmation-box">
            <header className="movement-confirmation-header">
              <div>
                <p className="eyebrow">CONTRÔLE AVANT VALIDATION</p>
                <h2 id="movement-confirmation-title">Confirmer le mouvement</h2>
              </div>
              <button
                aria-label="Fermer la confirmation"
                className="movement-confirmation-close-button"
                data-testid="close-movement-confirmation-button"
                disabled={submitting}
                onClick={() => setConfirming(false)}
                title="Fermer"
                type="button"
              >
                <X size={18} />
              </button>
            </header>
            <div className="movement-confirmation-scroll" data-testid="movement-confirmation-scroll">
              <p>
                Vous allez valider cette opération depuis {source?.name || "le dépôt sélectionné"}.
              </p>
              <dl>
                <div><dt>Type</dt><dd>{movementLabels[movement.movement_type]}</dd></div>
                <div><dt>Dépôt</dt><dd>{source?.name || "Non sélectionné"}</dd></div>
                {movement.movement_type === "transfert" && (
                  <div>
                    <dt>Destination</dt>
                    <dd>
                      {warehouses.find(
                        (warehouse) => warehouse.warehouse_id === movement.destination_warehouse_id,
                      )?.name || "Non sélectionnée"}
                    </dd>
                  </div>
                )}
                <div><dt>Produits</dt><dd>{movement.lines.length} ligne(s)</dd></div>
              </dl>
              <div className="movement-confirmation-lines" data-testid="movement-confirmation-lines">
                {movement.lines.map((line, index) => (
                  <div key={line.line_id || `${line.product_id}-${index}`}>
                    <strong>{productsById[line.product_id]?.name || "Produit"}</strong>
                    <span>{line.quantity} unité(s)</span>
                  </div>
                ))}
              </div>
            </div>
            <footer className="movement-confirmation-actions">
              <button
                className="secondary-action"
                data-testid="cancel-movement-confirmation-button"
                disabled={submitting}
                onClick={() => setConfirming(false)}
                type="button"
              >
                Annuler
              </button>
              <button
                className="primary-action submit-movement-button"
                data-testid="submit-movement-button"
                disabled={submitting}
                onClick={submit}
                ref={confirmationSubmitRef}
                type="button"
              >
                <Send size={16} /> {submitting ? "Validation…" : "Valider le mouvement"}
              </button>
            </footer>
          </section>
        </div>,
        document.body,
      )}
      <section className="data-panel movement-history-panel" data-testid="movement-history-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">HISTORIQUE</p>
            <h2>Derniers mouvements</h2>
          </div>
          <MoveHorizontal size={20} />
        </div>
        <div className="responsive-table">
          <table data-testid="movement-history-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Dépôt</th>
                <th>Produits</th>
                <th>Auteur</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr
                  className="interactive-history-row"
                  data-testid={`history-movement-${item.movement_id}`}
                  key={item.movement_id}
                  onClick={() => setSelectedMovementId(item.movement_id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedMovementId(item.movement_id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <td>
                    <span className={`movement-badge ${item.status === "annule" ? "annule" : item.movement_type}`}>
                      {item.status === "annule"
                        ? `Annulé · ${movementLabels[item.movement_type] || item.movement_type}`
                        : item.movement_type}
                    </span>
                  </td>
                  <td>{item.source_name}</td>
                  <td>{item.lines.length}</td>
                  <td>{item.author_name}</td>
                  <td>{new Date(item.movement_date || item.created_at).toLocaleDateString("fr-FR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {canCancelLastMovement && (
        <section className="cancel-last-movement-panel" data-testid="cancel-last-movement-panel">
          <div className="cancel-last-movement-heading">
            <div>
              <p className="eyebrow">ACTION ADMINISTRATEUR</p>
              <h2>Annuler le dernier mouvement du dépôt</h2>
            </div>
            <AlertTriangle aria-hidden="true" size={22} />
          </div>
          {!movement.warehouse_id && (
            <p data-testid="cancel-last-movement-select-warehouse-message">
              Sélectionnez un dépôt concerné pour examiner son dernier mouvement actif.
            </p>
          )}
          {movement.warehouse_id && isLatestCancellationLoading && (
            <p data-testid="cancel-last-movement-loading">Recherche du dernier mouvement…</p>
          )}
          {movement.warehouse_id && !isLatestCancellationLoading && !latestCancellableMovement && (
            <p data-testid="cancel-last-movement-empty-message">
              Aucun mouvement actif n’est disponible à l’annulation pour ce dépôt.
            </p>
          )}
          {latestCancellableMovement && !isLatestCancellationLoading && (
            <div className="cancel-last-movement-summary" data-testid="cancel-last-movement-summary">
              <div>
                <span>Dernier mouvement actif</span>
                <strong data-testid="cancel-last-movement-type">
                  {movementLabels[latestCancellableMovement.movement_type]}
                </strong>
                <small>{latestCancellableMovement.source_name}</small>
                {latestCancellableMovement.destination_name && (
                  <small>Vers {latestCancellableMovement.destination_name}</small>
                )}
              </div>
              <div className="cancel-last-movement-lines">
                {latestCancellableMovement.stock_preview?.map((line) => (
                  <article data-testid={`cancel-preview-${line.product_id}`} key={line.product_id}>
                    <strong>{line.product_name}</strong>
                    <span>Mouvement à annuler : {line.movement_quantity}</span>
                    {line.stock_effects.map((effect) => (
                      <small
                        className={effect.is_safe ? "" : "unsafe-cancellation-preview"}
                        data-testid={`cancel-preview-effect-${line.product_id}-${effect.warehouse_id}`}
                        key={effect.warehouse_id}
                      >
                        {effect.warehouse_name} : {effect.current_quantity} → {effect.quantity_after_cancellation}
                      </small>
                    ))}
                  </article>
                ))}
              </div>
              {!isCancellationConfirming && (
                <button
                  className="destructive-action"
                  data-testid="open-cancel-last-movement-confirmation-button"
                  disabled={!latestCancellableMovement.can_execute}
                  onClick={() => setIsCancellationConfirming(true)}
                  type="button"
                >
                  <RotateCcw size={16} /> Annuler ce mouvement
                </button>
              )}
              {!latestCancellableMovement.can_execute && (
                <p className="cancel-last-movement-warning" data-testid="cancel-last-movement-warning">
                  Les quantités actuelles ne permettent plus une annulation sûre.
                </p>
              )}
              {isCancellationConfirming && (
                <div className="cancellation-confirmation-box" data-testid="cancel-last-movement-confirmation-box">
                  <strong>Confirmer l’annulation définitive</strong>
                  <p>
                    Le stock sera restauré, le document sera supprimé et une trace d’annulation sera
                    conservée dans l’historique.
                  </p>
                  <div>
                    <button
                      data-testid="close-cancel-last-movement-confirmation-button"
                      onClick={() => setIsCancellationConfirming(false)}
                      type="button"
                    >
                      Retour
                    </button>
                    <button
                      className="destructive-action"
                      data-testid="confirm-cancel-last-movement-button"
                      onClick={cancelLastMovement}
                      type="button"
                    >
                      <AlertTriangle size={16} /> Annuler définitivement
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}
      <MovementDetailSheet
        movementId={selectedMovementId}
        onOpenChange={(open) => !open && setSelectedMovementId("")}
      />
    </div>
  );
}